import { Icon } from '@ui/assets/icons/IconWrapper';
import { Button } from '@ui/components/button';
import { Dropdown, Modal, Tooltip } from 'antd';
import classnames from 'classnames';
import React, { useMemo, useState } from 'react';
import {
    PatientFiltersProvider,
    useEmitFilters,
    useFilterGroupsActions,
    useFilterGroupsState,
    useFilterMetadata,
    useMultiSelectActions,
    useMultiSelectValues,
    useSyncInitialFilters,
} from '../hooks';
import type { PatientFiltersPanelProps } from '../types/filter-panel.types';
import { FilterGroupCard } from './FilterGroupCard';
import { FilterSummaryDisplay } from './FilterSummaryDisplay';
import Styles from './Styles.module.scss';

/**
 * Inner component that uses the context hooks
 */
function PatientFiltersPanelContent({
    onAdvancedFiltersChange,
    onFiltersChange,
    value,
    messageId,
    slotAfterToolbar,
}: PatientFiltersPanelProps) {
    const { filterGroups, setFilterGroups, showGroupOperatorSelection, setShowGroupOperatorSelection, containerRef } =
        useFilterGroupsState();
    const { addFilterGroup, clearFilters, toggleGroupOperator } = useFilterGroupsActions();
    const { multiSelectInValues, multiSelectOutValues, multiSelectOperator } = useMultiSelectValues();
    const { metadata } = useFilterMetadata();
    const { setFieldLabels, setMultiSelectInValues, setMultiSelectOutValues, setMultiSelectOperator } =
        useMultiSelectActions();
    const [isFilterSummaryModalOpen, setIsFilterSummaryModalOpen] = useState(false);

    // Sync initial filters to local state
    useSyncInitialFilters({
        initialAdvanced: value,
        metadata,
        filterGroups,
        setFilterGroups,
        setMultiSelectOperator,
        setMultiSelectInValues,
        setMultiSelectOutValues,
        setFieldLabels,
    });

    // Emit filter changes with debouncing
    useEmitFilters({
        filterGroups,
        metadata,
        multiSelectInValues,
        multiSelectOutValues,
        multiSelectOperator,
        onAdvancedFiltersChange,
        onFiltersChange,
    });

    const handleClearFilters = () => clearFilters(onFiltersChange);

    return (
        <div className={classnames('h-[calc(100vh-385px)] rounded-[12px] bg-white p-4 p-[12px]', Styles.mainContainer)}>
            <div className="mb-4 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <span className="text-[14px] font-semibold leading-[20px] text-[#656565]">Advanced Filtering</span>
                    <div className="flex items-center gap-2">
                        {filterGroups.length > 0 && (
                            <Tooltip
                                title="Filter Summary"
                                color="white"
                                placement="left"
                                styles={{ body: { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)', color: '#4D4D4D' } }}
                            >
                                <span
                                    className="flex cursor-pointer items-center"
                                    onClick={() => setIsFilterSummaryModalOpen(true)}
                                >
                                    <Icon
                                        name="FileIcon"
                                        size={24}
                                    />
                                </span>
                            </Tooltip>
                        )}
                    </div>
                </div>
                <div className="flex gap-2">
                    {filterGroups.length > 0 ? (
                        <Dropdown
                            popupRender={() => (
                                <div
                                    className="flex items-center justify-center gap-[10px] rounded-[8px] bg-white p-2 shadow-[0_14px_25.7px_4px_rgba(0,0,0,0.25)]"
                                    style={{ minWidth: '120px' }}
                                >
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={() => addFilterGroup('AND')}
                                            className={Styles.logicalOperatorButton}
                                        >
                                            AND
                                        </Button>
                                        <Button
                                            onClick={() => addFilterGroup('OR')}
                                            className={Styles.logicalOperatorButton}
                                        >
                                            OR
                                        </Button>
                                    </div>
                                </div>
                            )}
                            trigger={['click']}
                            open={showGroupOperatorSelection}
                            onOpenChange={setShowGroupOperatorSelection}
                        >
                            <Button
                                block
                                className="!h-[32px] !bg-[#E7F5FF] !text-[12px] !text-[#0098DA]"
                                icon={
                                    <Icon
                                        name="FilterIcon"
                                        size={20}
                                    />
                                }
                            >
                                Add Filter Group
                            </Button>
                        </Dropdown>
                    ) : (
                        <Button
                            block
                            className="!h-[32px] !bg-[#E7F5FF] !text-[12px] !text-[#0098DA]"
                            icon={
                                <Icon
                                    name="FilterIcon"
                                    size={20}
                                />
                            }
                            onClick={() => addFilterGroup()}
                        >
                            Add Filter Group
                        </Button>
                    )}
                    <Button
                        type="default"
                        onClick={handleClearFilters}
                    >
                        Clear All
                    </Button>
                </div>
                {slotAfterToolbar && <div className="mt-2">{slotAfterToolbar}</div>}
            </div>
            <div
                ref={containerRef}
                className="overflow-y-auto"
                style={{ maxHeight: 'calc(100vh - 550px)' }}
            >
                {filterGroups.length === 0 && (
                    <div className="flex h-[500px] w-full items-center justify-center">
                        <span className="px-12 text-center text-[12px] font-normal text-[#4E4E4E]">
                            No filter groups have been created yet.
                        </span>
                    </div>
                )}

                {filterGroups.map((group, groupIndex) => (
                    <React.Fragment key={group.id}>
                        {groupIndex > 0 && (
                            <div className="mb-[12px] flex justify-center">
                                <Button
                                    style={
                                        group.operator === 'AND'
                                            ? { backgroundColor: '#DCE0FB', color: '#203B87' }
                                            : { color: '#FF8400', backgroundColor: '#FFE8D0' }
                                    }
                                    className={classnames(
                                        Styles.logicalOperatorButton,
                                        group.operator === 'AND' ? Styles.logicalAndButton : Styles.logicalOrButton
                                    )}
                                    onClick={() => toggleGroupOperator(group.id)}
                                >
                                    {group.operator}
                                </Button>
                            </div>
                        )}
                        <FilterGroupCard
                            group={group}
                            groupIndex={groupIndex}
                        />
                    </React.Fragment>
                ))}
            </div>

            {/* Filter Summary Modal */}
            <Modal
                title={<div className="flex items-center justify-center text-[18px]">Filters</div>}
                open={isFilterSummaryModalOpen}
                onCancel={() => setIsFilterSummaryModalOpen(false)}
                footer={null}
                width={600}
            >
                <div className="max-h-[500px] overflow-y-auto p-3 text-[14px]">
                    <FilterSummaryDisplay
                        filterGroups={filterGroups}
                        metadata={metadata}
                        multiSelectInValues={multiSelectInValues}
                        multiSelectOutValues={multiSelectOutValues}
                    />
                </div>
            </Modal>
        </div>
    );
}

/**
 * Main component wrapped with PatientFiltersProvider
 */
export function PatientFiltersPanel({
    value,
    onAdvancedFiltersChange,
    onFiltersChange,
    initialFilters,
    messageId,
    slotAfterToolbar,
}: PatientFiltersPanelProps) {
    // Handle wrapped filter structure: { mode: "standard", filter: {...} }
    const unwrappedValue = useMemo(() => {
        if (!value) return undefined;
        // If filter has mode/filter wrapper, extract the inner filter
        if ((value as any).mode && (value as any).filter) {
            return (value as any).filter;
        }
        return value;
    }, [value]);

    return (
        <PatientFiltersProvider
            initialAdvanced={unwrappedValue}
            initialFilters={initialFilters}
        >
            <PatientFiltersPanelContent
                value={unwrappedValue}
                onAdvancedFiltersChange={onAdvancedFiltersChange}
                onFiltersChange={onFiltersChange}
                messageId={messageId}
                slotAfterToolbar={slotAfterToolbar}
            />
        </PatientFiltersProvider>
    );
}
